import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Bell, Command, FlaskConical, LayoutGrid, ListTodo, Search as SearchIcon, Sparkles } from "lucide-react";
import LogoKearneyPerlab from "@/assets/svg/logoKearneyPerlab.svg?react";
import { useResponsiveTier } from "@perlab/ui";
import { ZARVIS_APP_ID } from "@perlab/registry";
import { ContextChain, navTo } from "./ContextChain";
import { AddSlot } from "./AddSlot";
import { Popover } from "./Popover";
import { useContextChain } from "../state/ContextChain";
import { useShellStore } from "../state/shellStore";
import { useFlag } from "../flags/FlagsContext";
import { AccountMenu, HelpMenu } from "./TopBarMenus";
import { AppBarActions } from "./AppBarActions";
import { MyWorkPage } from "../pages/MyWorkPage";
import { AppLauncherPanel } from "./AppLauncher";
import { SearchPage } from "../pages/SearchPage";
import { NotificationsPage } from "../pages/NotificationsPage";
import { SendToGlean } from "../glean/SendToGlean";

// Top bar = brand (logo) + breadcrumb (left) · shell-feature toolbar (right). EVERY toolbar
// interaction is a FLOATING overlay — popover panels (My Work / Search / Notifications),
// the command palette (Ask AI), and menus (Help / Account) — never an inline canvas page.
export function TopNav() {
  const { workspaceId, appId } = useContextChain();
  const { tier } = useResponsiveTier();
  const navigate = useNavigate();
  const openCommandPalette = useShellStore((s) => s.openCommandPalette);
  const toggleAskAi = useShellStore((s) => s.toggleAskAi);
  const closeAskAi = useShellStore((s) => s.closeAskAi);
  const askAiOpen = useShellStore((s) => s.askAiOpen);
  const showNotifications = useFlag("shell.notifications", true);
  const unread = useUnreadCount(workspaceId, showNotifications);

  // Brand "home" = the CURRENT workspace's home, mirroring the sidebar workspace chip — NOT "/".
  // "/" routes through the index redirect, which targets the hardcoded DEFAULT_SCOPE.ws; when that
  // workspace isn't in the tree the app bounces through a non-existent workspace, and that bounce
  // crosses a workspace change → syncScope FULL-resets the breadcrumb trail. Going straight to the
  // current workspace home keeps the same-workspace trail (one-click return), consistent with the
  // chip/switcher. Falls back to "/" only when there is no current workspace yet.
  const homeTo = workspaceId ? navTo.workspaceHome(workspaceId) : ({ to: "/" } as const);

  return (
    <div className="h-full flex items-center px-3 gap-3 min-w-0">
      <Link
        {...homeTo}
        aria-label="PERLab Velocity home"
        className="flex items-center gap-3 text-[var(--color-text-primary)] shrink-0 hover:opacity-80"
      >
        {/* Logo + wordmark sized for the 48px bar; divider sits between the logo and "Velocity".
            Mobile tier: icon-only fallback (RD-6 rendered proof 2026-07-19) — the full wordmark +
            toolbar are both rigid, and together they exceed a 390px viewport, forcing page-level
            horizontal overflow. The logo alone stays; the Link keeps its accessible name. */}
        <LogoKearneyPerlab className="h-[23px] w-auto" role="img" aria-label="Kearney PERLab" />
        {tier !== "mobile" && (
        <>
        <span aria-hidden className="h-4 w-px shrink-0 bg-[var(--color-border-strong)]" />
        <span className="text-[15px] tracking-wide">
          <span className="font-semibold">Velocity</span>{" "}
          <span className="font-light text-[var(--color-text-secondary)]">Workspace</span>
        </span>
        </>
        )}
      </Link>
      {/* Divider separates the PRODUCT brand from the breadcrumb — same gap as the logo│name divider
          (parent gap-3 = 12px each side, matching the brand Link's internal gap-3). */}
      <span aria-hidden className="h-4 w-px shrink-0 bg-[var(--color-border-strong)]" />
      <ContextChain />
      {/* The ONE "+ Add" door (spec 48.24 §5) — switch / create / go-to / apps for the current node.
          Retained while an app is open (crumbs are click-only). Sits at the breadcrumb tail. */}
      <AddSlot />

      {/* Row 1.12 — contextual actions for the currently-mounted app (if any registered any).
          Distinct from the generic shell toolbar below (My Work/Search/.../Ask AI), which is
          always app-independent. */}
      <AppBarActions />

      <div className="ml-auto flex min-w-0 items-center gap-0.5">
        <WorkspaceActions>
        {/* Row 5.3: App launcher/Search/Notifications move to BottomTabBar at mobile tier —
            hidden here so the primary mobile nav surface isn't duplicated (a Strategist finding
            from this batch's post-build critique: the mobile tab bar re-added these as separate
            controls without ever hiding TopNav's originals). My Work/Go-to/Help/Ask AI
            have no BottomTabBar equivalent, so they stay visible at every tier. */}
        {tier !== "mobile" && (
          <>
            {/* App launcher (knowledge/09) — waffle → level-aware apps + data-readiness. The panel
                mounts only while open (Popover lazy children), so readiness probes are fetch-on-open. */}
            <Popover
              ariaLabel="App launcher"
              placement="bottom-end"
              panelClassName="max-h-[80vh] overflow-y-auto"
              trigger={({ toggle }) => (
                <TbIconButton label="App launcher" onClick={toggle}>
                  <LayoutGrid size={16} aria-hidden />
                </TbIconButton>
              )}
            >
              {(close) => <AppLauncherPanel close={close} />}
            </Popover>
          </>
        )}

        <TbIconButton label="SandBox" onClick={() => navigate(navTo.sandbox(workspaceId) as never)}>
          <FlaskConical size={16} aria-hidden />
        </TbIconButton>

        <Popover
          ariaLabel="My Work"
          placement="bottom-end"
          panelClassName="w-96 max-h-[80vh] overflow-y-auto"
          trigger={({ toggle }) => (
            <TbIconButton label="My Work" onClick={toggle}>
              <ListTodo size={16} aria-hidden />
            </TbIconButton>
          )}
        >
          <MyWorkPage />
        </Popover>

        {tier !== "mobile" && (
          <>
            <Popover
              ariaLabel="Search"
              placement="bottom-end"
              panelClassName="w-96 max-h-[80vh] overflow-y-auto"
              trigger={({ toggle }) => (
                <TbIconButton label="Search" onClick={toggle}>
                  <SearchIcon size={16} aria-hidden />
                </TbIconButton>
              )}
            >
              <SearchPage />
            </Popover>

            {showNotifications && (
              <Popover
                ariaLabel="Notifications"
                placement="bottom-end"
                panelClassName="w-96 max-h-[80vh] overflow-y-auto"
                trigger={({ toggle }) => (
                  <TbIconButton label="Notifications" badge={unread} onClick={toggle}>
                    <Bell size={16} aria-hidden />
                  </TbIconButton>
                )}
              >
                <NotificationsPage />
              </Popover>
            )}
          </>
        )}

        {/* Command palette — navigates to apps/pages ("Go to…", also ⌘K). Honest label:
            this is NOT AI. Real AI grounding is the deferred engine seam (CFR-026). */}
        <TbIconButton label="Go to…" onClick={openCommandPalette}>
          <Command size={16} aria-hidden />
        </TbIconButton>
        <HelpMenu />
        <SendToGlean />
        {/* Only the global launcher uses the orange AI glyph. Assistant identity avatars remain
            available inside the drawer and app-scoped navigation surfaces. */}
        <TbIconButton
          label={askAiOpen ? "Close Ask AI" : "Ask AI"}
          onClick={appId === ZARVIS_APP_ID ? closeAskAi : toggleAskAi}
          active={askAiOpen || appId === ZARVIS_APP_ID}
        >
          <Sparkles
            size={17}
            aria-hidden
            data-testid="ask-ai-launcher-icon"
            className="text-[var(--color-accent)]"
          />
        </TbIconButton>
        </WorkspaceActions>
        {/* Account identity is pinned outside the horizontally scrollable actions so the user's
            avatar remains the stable far-right control at every responsive tier. */}
        <AccountMenu />
      </div>
    </div>
  );
}

function WorkspaceActions({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const updateOverflow = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const canScroll = scroller.scrollWidth > scroller.clientWidth + 1;
    const left = canScroll && scroller.scrollLeft > 1;
    const right = canScroll && scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
    setOverflow((current) => (
      current.left === left && current.right === right ? current : { left, right }
    ));
  }, []);

  useEffect(() => {
    updateOverflow();
    if (typeof ResizeObserver === "undefined" || !scrollerRef.current) return undefined;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(scrollerRef.current);
    return () => observer.disconnect();
  }, [updateOverflow]);

  return (
    <div className="relative min-w-0 flex-1">
      <div
        ref={scrollerRef}
        role="group"
        aria-label="Workspace actions"
        title={overflow.left || overflow.right ? "Scroll for more workspace actions" : undefined}
        onScroll={updateOverflow}
        className="flex min-w-0 items-center gap-0.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-[var(--surface-sunken)] to-transparent transition-opacity motion-reduce:transition-none ${overflow.left ? "opacity-100" : "opacity-0"}`}
      />
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-[var(--surface-sunken)] to-transparent transition-opacity motion-reduce:transition-none ${overflow.right ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}

function TbIconButton({
  label,
  onClick,
  badge,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  badge?: number;
  active?: boolean;
  children: React.ReactNode;
}) {
  // H4.5 (INSTR-051 §12 a11y): when a count badge is present, fold it into the button's ACCESSIBLE
  // NAME so a screen reader announces "Notifications, 4 unread" — not just "Notifications" (the
  // explicit aria-label otherwise masks the visual badge text). Only badged buttons get the suffix;
  // unbadged ones keep their plain label unchanged.
  const badgeText = badge !== undefined && badge > 0 ? (badge > 99 ? "99+" : String(badge)) : null;
  const accessibleName = badgeText ? `${label}, ${badgeText} unread` : label;
  return (
    <button
      type="button"
      aria-label={accessibleName}
      title={label}
      aria-pressed={active}
      onClick={onClick}
      className={
        // 44px hit-area (row 5.5, WCAG 2.5.5) via padding — icon glyph (size=16) is unchanged,
        // only the transparent-at-rest/hoverable box grows.
        "relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors " +
        (active
          ? "bg-[var(--color-orange-15)] text-[var(--color-orange-55)]"
          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]")
      }
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-danger,#e5484d)] px-1 text-[10px] font-semibold leading-none text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}

// Best-effort unread badge — counts the actor's unread notifications for the active workspace.
function useUnreadCount(workspaceId: string, enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled || !workspaceId) {
      setCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // H3.4 (H3.3 D1): the CANONICAL unread count — an uncapped server count, not the length of a
        // list capped at _MAX_RESULTS. Same recipient+workspace(+globals) predicate the list uses, so
        // this badge and the NotificationsPage header render ONE authoritative number.
        const res = await fetch(
          `/api/workspace/notifications/unread-count?workspace_id=${encodeURIComponent(workspaceId)}`,
          { method: "GET" },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data?.unread_count === "number") setCount(data.unread_count);
      } catch {
        /* badge is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, enabled]);
  return count;
}
